import "@/App.css";
import "@/portal/i18n";
import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { ThemeProvider } from "@/context/ThemeContext";
import { AuthProvider } from "@/context/AuthContext";
import { Toaster } from "@/components/ui/sonner";

import LoginPage from "@/pages/Login";
import { AppShell } from "@/components/AppShell";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { PortalAuthProvider } from "@/portal/PortalAuthContext";

// Lazily load every non-critical page so the initial bundle stays small.
// Each route only downloads + parses its own JSX bundle when first visited,
// which dramatically reduces TTI and the cost of every subsequent
// navigation / modal open.
// `webpackPrefetch: true` tells webpack to add <link rel="prefetch"> for each chunk so the
// browser silently downloads them during idle time AFTER the initial bundle parses. This
// eliminates the "click → spinner → render" delay on subsequent route changes (which was
// noticeable after a fresh deploy when no chunk is cached yet).
const ApplyPage = lazy(() => import(/* webpackPrefetch: true */ "@/pages/Apply"));
const PublicDailyRevenuePage = lazy(() =>
  import(/* webpackPrefetch: true */ "@/pages/PublicDailyRevenue"),
);
const DashboardPage = lazy(() => import(/* webpackPrefetch: true */ "@/pages/Dashboard"));
const UsersPage = lazy(() => import(/* webpackPrefetch: true */ "@/pages/Users"));
const EmployeesPage = lazy(() => import(/* webpackPrefetch: true */ "@/pages/Employees"));
const EmployeeDetailPage = lazy(() => import(/* webpackPrefetch: true */ "@/pages/EmployeeDetail"));
const CompaniesPage = lazy(() => import(/* webpackPrefetch: true */ "@/pages/Companies"));
const CompanyDetailPage = lazy(() => import(/* webpackPrefetch: true */ "@/pages/CompanyDetail"));
const SiteDetailPage = lazy(() => import(/* webpackPrefetch: true */ "@/pages/SiteDetail"));
const InspectionDetailPage = lazy(() => import(/* webpackPrefetch: true */ "@/pages/InspectionDetail"));
const RecruitmentPage = lazy(() => import(/* webpackPrefetch: true */ "@/pages/Recruitment"));
const RecruitmentInterviewsPage = lazy(() =>
  import(/* webpackPrefetch: true */ "@/pages/RecruitmentInterviews"),
);
const PositionDetailPage = lazy(() => import(/* webpackPrefetch: true */ "@/pages/PositionDetail"));
const SupplierProductsPage = lazy(() => import(/* webpackPrefetch: true */ "@/pages/SupplierProducts"));
const SupplierProductDetailPage = lazy(() =>
  import(/* webpackPrefetch: true */ "@/pages/SupplierProductDetail"),
);
const SupplierOrdersPage = lazy(() => import(/* webpackPrefetch: true */ "@/pages/SupplierOrders"));
const SupplierOrderDetailPage = lazy(() => import(/* webpackPrefetch: true */ "@/pages/SupplierOrderDetail"));
const DailyRevenuesPage = lazy(() =>
  import(/* webpackPrefetch: true */ "@/pages/daily_revenues/DailyRevenuesPage"),
);
const DailyRevenueDetailPage = lazy(() =>
  import(/* webpackPrefetch: true */ "@/pages/daily_revenues/DailyRevenueDetailPage"),
);
const CashRegistersPage = lazy(() => import(/* webpackPrefetch: true */ "@/pages/CashRegistersPage"));
const PartnersPage = lazy(() => import(/* webpackPrefetch: true */ "@/pages/Partners"));
const PartnerDetailPage = lazy(() => import(/* webpackPrefetch: true */ "@/pages/PartnerDetail"));
const SubscriptionsPage = lazy(() => import(/* webpackPrefetch: true */ "@/pages/Subscriptions"));
const MaintenancesPage = lazy(() =>
  import(/* webpackPrefetch: true */ "@/pages/Maintenances").then((m) => ({ default: m.default })),
);
const SchedulePage = lazy(() =>
  import(/* webpackPrefetch: true */ "@/pages/schedule/SchedulePage"),
);
const MaintenanceDetailPage = lazy(() =>
  import(/* webpackPrefetch: true */ "@/pages/Maintenances").then((m) => ({ default: m.MaintenanceDetailPage })),
);
const AccidentsPage = lazy(() =>
  import(/* webpackPrefetch: true */ "@/pages/Accidents").then((m) => ({ default: m.default })),
);
const AccidentDetailPage = lazy(() =>
  import(/* webpackPrefetch: true */ "@/pages/Accidents").then((m) => ({ default: m.AccidentDetailPage })),
);
const InfluencersPage = lazy(() =>
  import(/* webpackPrefetch: true */ "@/pages/Influencers").then((m) => ({ default: m.default })),
);
const InfluencerDetailPage = lazy(() =>
  import(/* webpackPrefetch: true */ "@/pages/Influencers").then((m) => ({ default: m.InfluencerDetailPage })),
);
const NotificationsPage = lazy(() => import(/* webpackPrefetch: true */ "@/pages/Notifications"));
const StatisticsPage = lazy(() => import(/* webpackPrefetch: true */ "@/pages/Statistics"));

// ---- Dolgozói portál (/dolgozo) ----
// NOTE: A portál oldalak NEM lazy-load-olva — a portál különálló, kicsi modul,
// és a route-váltások közötti Suspense fallback "villanás" érezhető lag-et
// okozott mobilon. Egyetlen chunk → instant nav.
import { PortalShell } from "@/portal/PortalShell";
import PortalLogin from "@/portal/PortalLogin";
import PortalDashboard from "@/portal/PortalDashboard";
import PortalSchedule from "@/portal/PortalSchedule";
import PortalFinances from "@/portal/PortalFinances";
import PortalWorkwear from "@/portal/PortalWorkwear";
import PortalEmployment from "@/portal/PortalEmployment";
import PortalDailyClosing from "@/portal/PortalDailyClosing";
import PortalDrafts from "@/portal/PortalDrafts";
import PortalProfile from "@/portal/PortalProfile";

// Minimal silent fallback — most chunks load in <100 ms on a warm connection.
// We avoid a spinner here to prevent a flash between fast route changes; the
// individual pages already render their own skeletons while data loads.
function RouteFallback() {
  return <div data-testid="route-loading" className="min-h-[40vh]" />;
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <Suspense fallback={<RouteFallback />}>
            <Routes>
              <Route path="/login" element={<LoginPage />} />
              <Route path="/apply/:slug" element={<ApplyPage />} />
              <Route
                path="/dr/:slug"
                element={<PublicDailyRevenuePage />}
              />
              <Route
                element={
                  <ProtectedRoute>
                    <AppShell />
                  </ProtectedRoute>
                }
              >
                <Route index element={<Navigate to="/dashboard" replace />} />
                <Route path="/dashboard" element={<DashboardPage />} />
                <Route path="/users" element={<UsersPage />} />
                <Route path="/companies" element={<CompaniesPage />} />
                <Route path="/companies/:id" element={<CompanyDetailPage />} />
                <Route path="/sites/:id" element={<SiteDetailPage />} />
                <Route path="/inspections/:id" element={<InspectionDetailPage />} />
                <Route path="/employees" element={<EmployeesPage />} />
                <Route path="/employees/:id" element={<EmployeeDetailPage />} />
                <Route path="/recruitment" element={<RecruitmentPage />} />
                <Route
                  path="/recruitment/interviews"
                  element={<RecruitmentInterviewsPage />}
                />
                <Route
                  path="/recruitment/positions/:id"
                  element={<PositionDetailPage />}
                />
                <Route path="/supplier-products" element={<SupplierProductsPage />} />
                <Route
                  path="/supplier-products/:id"
                  element={<SupplierProductDetailPage />}
                />
                <Route path="/supplier-orders" element={<SupplierOrdersPage />} />
                <Route
                  path="/supplier-orders/:id"
                  element={<SupplierOrderDetailPage />}
                />
                <Route path="/daily-revenues" element={<DailyRevenuesPage />} />
                <Route
                  path="/daily-revenues/:id"
                  element={<DailyRevenueDetailPage />}
                />
                <Route path="/cash-registers" element={<CashRegistersPage />} />
                <Route path="/partners" element={<PartnersPage />} />
                <Route path="/partners/:id" element={<PartnerDetailPage />} />
                <Route path="/subscriptions" element={<SubscriptionsPage />} />
                <Route path="/maintenances" element={<MaintenancesPage />} />
                <Route path="/schedule" element={<SchedulePage />} />
                <Route
                  path="/maintenances/:id"
                  element={<MaintenanceDetailPage />}
                />
                <Route path="/accidents" element={<AccidentsPage />} />
                <Route path="/accidents/:id" element={<AccidentDetailPage />} />
                <Route path="/influencers" element={<InfluencersPage />} />
                <Route path="/influencers/:id" element={<InfluencerDetailPage />} />
                <Route path="/notifications" element={<NotificationsPage />} />
                <Route path="/statistics" element={<StatisticsPage />} />
              </Route>
              {/* ---- Dolgozói portál (különálló auth) ---- */}
              <Route
                path="/dolgozo/*"
                element={
                  <PortalAuthProvider>
                    <Routes>
                      <Route path="login" element={<PortalLogin />} />
                      <Route element={<PortalShell />}>
                        <Route index element={<PortalDashboard />} />
                        <Route path="beosztas" element={<PortalSchedule />} />
                        <Route path="penzugyek" element={<PortalFinances />} />
                        <Route path="munkaruha" element={<PortalWorkwear />} />
                        <Route path="munkaviszony" element={<PortalEmployment />} />
                        <Route path="napi-zaras" element={<PortalDailyClosing />} />
                        <Route path="tervezet" element={<PortalDrafts />} />
                        <Route path="profil" element={<PortalProfile />} />
                      </Route>
                      <Route path="*" element={<Navigate to="/dolgozo" replace />} />
                    </Routes>
                  </PortalAuthProvider>
                }
              />
              <Route path="*" element={<Navigate to="/dashboard" replace />} />
            </Routes>
          </Suspense>
        </BrowserRouter>
        <Toaster
          position="top-right"
          offset={{ top: "max(env(safe-area-inset-top), 16px)", right: "16px" }}
          mobileOffset={{ top: "max(env(safe-area-inset-top), 16px)", right: "12px", left: "12px" }}
        />
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
